import { useRef, useState } from "react";
import clsx from "classnames";
import { isAndroid, isMacOs, isWindows } from "react-device-detect";
import { Dialog } from "../../core/Dialog/Dialog";
import { StaticImage } from "../../core/StaticImage/StaticImage";
import { getPlatformIcon } from "../../../models/media";
import { TypographyVariant } from "../../../constants/typography";
import { Typography } from "../../core/Typography/Typography";
import { MAX_WIDTH_MOBILE } from "../../../constants/dimension";
import { TypePopup } from "../../../types/popup";
import { SetupDevicesPopup } from "../SetupDevicesPopup";
import { pwaInstall } from "../../../models/system";
import { cache } from "../../../constants/pwa";
import { useStyles } from "./DownloadPopup.style";
import { useGamepadContext } from "../../../contexts/GamepadProvider";
import { isTV } from "../../../utils/platform";
import { useTranslation } from "react-i18next";
import { PwaInstallToast } from "../PwaInstallToast/PwaInstallToast";

type IDownloadPopup = {
  onClose?: () => void;
  open: boolean;
};

const DownloadPopup: React.FC<IDownloadPopup> = (props) => {
  const { onClose, open } = props;
  const classes = useStyles();
  const { t } = useTranslation();
  const [typePopup, setTypePopup] = useState<TypePopup>("");
  const [isPwaInstallToastOpen, setIsPwaInstallToastOpen] = useState(false);
  const isMobile = window.innerWidth <= MAX_WIDTH_MOBILE;
  const { setActiveElement } = useGamepadContext();
  const lastActiveCardRef = useRef<string>("");
  const isRootPopup = typePopup === "";

  const setIsInstallPwa = () => {
    localStorage.setItem("isHidePwa", "true");
  };

  const onGetApp = async (platform: TypePopup, platformId: string) => {
    const hasAndroid = platform === "android" && isAndroid;
    const hasMacos = platform === "osx" && isMacOs;
    const hasWindows = platform === "windows" && isWindows;
    const pwaAlready = cache["root"];
    if ((hasAndroid || hasMacos || hasWindows) && pwaAlready) {
      await pwaInstall(setIsInstallPwa, () => setIsPwaInstallToastOpen(true));
      return;
    }
    if (platform === "google") {
      window.open(
        "https://play.google.com/store/apps/details?id=com.onmo.plus",
        "_blank",
      );
      return;
    }
    if (platform === "samsung") {
      window.open(
        "https://www.samsung.com/us/tvs/smart-tv/samsung-tv-apps-detail/?appId=3202605045318",
        "_blank",
      );
      return;
    }
    if (platform === "lg") {
      window.open(
        "https://in.lgappstv.com/main/tvapp/detail?appId=1303471",
        "_blank",
      );
      return;
    }
    lastActiveCardRef.current = platformId;
    const activeBtn = document.querySelector(
      '[data-gamepad="button-default"]',
    ) as HTMLElement;
    setActiveElement(activeBtn);
    setTypePopup(platform);
  };

  const onClosePopup = () => {
    if (typePopup !== "") {
      setTypePopup("");
      requestAnimationFrame(() => {
        const card = document.querySelector(
          `[data-platform-id="${lastActiveCardRef.current}"]`,
        ) as HTMLElement;
        if (card) {
          setActiveElement(card);
        }
      });
    } else {
      onClose?.();
    }
  };

  const renderPlatformCard = (platformId: string, isHideSelect?: boolean) => {
    const platform = getPlatformIcon(platformId);
    return (
      <div
        className={clsx(
          classes.platformCard,
          isHideSelect && classes.hideSelect,
        )}
        onClick={() => onGetApp(platform?.typePopup || "", platformId)}
        data-gamepad={isHideSelect ? null : "platform-card"}
        data-selected-class={classes.selected}
        data-platform-id={platformId}
      >
        <StaticImage
          src={platform.icon}
          src2x={platform?.icon2x}
          src3x={platform.icon3x}
          className={classes.logoIcon}
        />
        <div className={classes.cardContent}>
          <Typography variant={TypographyVariant.BodyB6Regular}>
            {t(platform.labelKey)}
          </Typography>
          <Typography variant={TypographyVariant.BodyB4Medium}>
            {platform.name}
          </Typography>
        </div>
      </div>
    );
  };

  const renderContent = () => {
    if (isRootPopup) {
      return (
        <>
          <div className={classes.header}>
            <Typography
              variant={
                isMobile
                  ? TypographyVariant.BodyB2Bold
                  : TypographyVariant.HeadingH3Bold
              }
            >
              {t("Download_Download ONMO+")}
            </Typography>
            <Typography
              variant={
                isMobile
                  ? TypographyVariant.BodyB6Medium
                  : TypographyVariant.BodyB3Medium
              }
            >
              {t(
                "Download_Download our app and easily play on your TV, desktop or mobile devices!",
              )}
            </Typography>
          </div>
          <div className={classes.container}>
            <div className={classes.item}>
              <Typography variant={TypographyVariant.BodyB3Medium}>
                {t("Download_Mobile")}
              </Typography>
              <div className={classes.platforms}>
                {renderPlatformCard("android", isTV)}
                {renderPlatformCard("appleStore", isTV)}
              </div>
            </div>
            <div className={classes.item}>
              <Typography variant={TypographyVariant.BodyB3Medium}>
                {t("Download_Desktop")}
              </Typography>
              <div className={classes.platforms}>
                {renderPlatformCard("microsoftStore", isTV)}
                {renderPlatformCard("appleStoreDesktop", isTV)}
              </div>
            </div>
            <div className={classes.item}>
              <Typography variant={TypographyVariant.BodyB3Medium}>
                {t("Download_TV")}
              </Typography>
              <div className={classes.platforms}>
                {renderPlatformCard("googlePlay", isTV)}
                {renderPlatformCard("lgTV", isTV)}
                {renderPlatformCard("samsungTV", isTV)}
              </div>
            </div>
          </div>
        </>
      );
    }
    return <SetupDevicesPopup type={typePopup} />;
  };

  return (
    <Dialog
      open={open}
      onClose={onClosePopup}
      mainAction={{
        text: t("Download_Close"),
        onClick: onClosePopup,
        id: isRootPopup ? "close-dialog" : "",
        size: "large",
        "data-button-sound": "back",
      }}
      className={classes.dialog}
      rootClassName={clsx(isRootPopup && classes.root)}
    >
      {renderContent()}
      <PwaInstallToast
        open={isPwaInstallToastOpen}
        onClose={() => setIsPwaInstallToastOpen(false)}
      />
    </Dialog>
  );
};
export default DownloadPopup;
